import React, { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { BookOpen, Play, Terminal } from 'lucide-react'
import { AvailableModules } from './ui/AvailableModules'
import { useUser } from '../context/UserContext'
import { usePageHeader } from '../context/PageHeaderContext'
import { coursesAPI } from '../api/courses'
import { appInfo } from '@renderer/config/appinfo'
import iconSrc from '../../../../resources/icon.png'
import { formatDuration } from '../utils/duration'
import type { LabModule } from '../types/api'
import { useBreadcrumbs } from '@renderer/context/BreadcrumbContext'

interface LabModuleWithProgress extends LabModule {
  status?: string
  completed_exercises?: number
  total_exercises?: number
}

export const LabModulesPage = (): React.JSX.Element => {
  const { courseId } = useParams<{ courseId: string }>()
  const navigate = useNavigate()
  const { user } = useUser()
  const { setSubtitle } = usePageHeader()
  const { setBreadcrumbs } = useBreadcrumbs()

  const [modules, setModules] = useState<LabModuleWithProgress[]>([])
  const [loading, setLoading] = useState(true)
  const [courseName, setCourseName] = useState<string>('')
  const [courseDescription, setCourseDescription] = useState<string>('')
  const [courseLevel, setCourseLevel] = useState<string>('')
  const [courseThumbnail, setCourseThumbnail] = useState<string | null>(null)
  const [totalDurationMinutes, setTotalDurationMinutes] = useState<number>(0)

  useEffect(() => {
    const fetchData = async (): Promise<void> => {
      if (!courseId) return
      try {
        const courseData = await coursesAPI.getCourse(courseId)
        const name = courseData.title || 'Course'
        setCourseName(name)
        setBreadcrumbs([
          { label: 'Courses', path: '/courses' },
          { label: name, path: `/courses/${courseId}` }
        ])
        setCourseDescription(courseData.description || '')
        setCourseLevel(courseData.level || 'Technical Track')
        setCourseThumbnail(courseData.thumbnail || null)
      } catch {
        // ignore — AvailableModules handles its own error state
      } finally {
        setLoading(false)
      }
    }
    fetchData()
  }, [courseId, user?.id, setSubtitle])

  const completedModules = modules.filter((m) => (m.progress ?? 0) >= 100).length
  const totalModules = modules.length
  const overallProgress =
    totalModules > 0
      ? Math.round(modules.reduce((sum, m) => sum + (m.progress ?? 0), 0) / totalModules)
      : 0

  const nextModule = modules.find((m) => (m.progress ?? 0) < 100) ?? modules[0]
  const heroImage = courseThumbnail ? `${appInfo.apiDomain}/storage/${courseThumbnail}` : iconSrc

  const ringRadius = 27
  const ringCircumference = 2 * Math.PI * ringRadius

  return (
    <div>
      <div className="max-w-[1200px] mx-auto space-y-5">
        <div className="relative w-full overflow-hidden rounded-2xl shadow-lg">
          <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: `url('${heroImage}')` }} />
          <div
            className="absolute inset-0"
            style={{ background: 'linear-gradient(135deg, rgba(36,21,0,0.8) 0%, rgba(0,0,0,0.8) 100%)' }}
          />
          <div
            className="relative z-10 flex flex-col p-4 sm:p-5"
            style={{ minHeight: 'clamp(240px, 24vw, 320px)' }}
          >
            <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 flex-1">
              <div className="flex flex-col min-w-0">
                <span className="w-fit inline-flex items-center gap-2 rounded-full border border-white/30 bg-white/10 px-2.5 py-1 text-[11px] font-semibold text-white mb-2.5">
                  {courseLevel}
                </span>

                <h1
                  className="font-bold text-white mb-1.5"
                  style={{ fontSize: 'clamp(1.05rem, 2vw, 1.5rem)' }}
                >
                  {courseName}
                </h1>

                <p className="max-w-xl mb-2.5 text-xs leading-relaxed text-white/70 line-clamp-2">
                  {courseDescription ||
                    (totalDurationMinutes > 0
                      ? `${formatDuration(totalDurationMinutes)} of hands-on lab exercises across all modules.`
                      : 'Explore the lab modules for this course and track your progress through hands-on exercises.')}
                </p>

                {!loading && totalModules > 0 && (
                  <div className="flex items-center gap-4 text-[11px] text-white/60">
                    <span className="flex items-center gap-1.5">
                      <BookOpen className="w-3.5 h-3.5" />
                      {totalModules} modules
                    </span>
                    <span className="flex items-center gap-1.5">
                      <Terminal className="w-3.5 h-3.5" />
                      Hands-on labs
                    </span>
                  </div>
                )}
              </div>

              {!loading && totalModules > 0 && (
                <div
                  className="shrink-0 flex flex-col items-stretch gap-3 rounded-xl p-4"
                  style={{ background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.15)' }}
                >
                  <div className="flex items-center gap-3">
                    <svg width="60" height="60" viewBox="0 0 64 64" className="shrink-0">
                      <circle cx="32" cy="32" r={ringRadius} fill="none" stroke="rgba(255,255,255,0.15)" strokeWidth="6" />
                      <circle
                        cx="32"
                        cy="32"
                        r={ringRadius}
                        fill="none"
                        stroke="#D4AF37"
                        strokeWidth="6"
                        strokeLinecap="round"
                        strokeDasharray={ringCircumference}
                        strokeDashoffset={ringCircumference * (1 - overallProgress / 100)}
                        transform="rotate(-90 32 32)"
                      />
                      <text x="32" y="37" textAnchor="middle" fontSize="15" fontWeight="700" fill="#ffffff">
                        {overallProgress}%
                      </text>
                    </svg>
                    <div>
                      <p className="text-[10px] uppercase tracking-wide font-semibold text-white/50">Course progress</p>
                      <p className="text-xs text-white/70 mt-0.5">{completedModules} of {totalModules} modules completed</p>
                    </div>
                  </div>
                  {nextModule && (
                    <button
                      onClick={() => navigate(`/course/${courseId}/module/${nextModule.id}/exercises`)}
                      className="flex items-center justify-center gap-1.5 rounded-full px-4 py-2 text-xs font-semibold text-white shadow-sm transition hover:opacity-90"
                      style={{ background: '#7a6020' }}
                    >
                      <Play className="w-3 h-3 fill-white" />
                      {overallProgress === 0 ? 'Start Learning' : overallProgress >= 100 ? 'Review Modules' : 'Continue Learning'}
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>

        <AvailableModules
          courseId={courseId}
          onModulesLoaded={setModules}
          onTotalDurationMinutes={setTotalDurationMinutes}
        />
      </div>
    </div>
  )
}
